import React, { FC, ReactNode, useState } from "react";
import { Box, Collapse, Link, MenuItem, Paper, Select, Typography } from "@mui/material";
import { usePricing, TierRow, LicenseRow, PackageRow } from "./PricingContext";
import { DotLottleLoader } from "../../../components/Loader";
import { formatCurrency } from "../../../utils/formatCurrency";
import { BaseRecipeTable } from "../../../components/Pricing/BaseRecipeTable";
import { titleizeName } from "../../../utils";

// Page gold — matches the underline-variant tabs.
const GOLD = "var(--accent)";

// Plain explainer paragraph. Copy is passed as JS expressions so apostrophes
// don't trip react/no-unescaped-entities.
const Para: FC<{ children: ReactNode }> = ({ children }) => (
    <Typography color="var(--text-2)" sx={{ fontSize: 14, lineHeight: 1.7, mb: 1.5 }}>
        {children}
    </Typography>
);

// `featured` renders the card on a soft gold wash with a gold border — used to
// spotlight the distinct-vs-matching discount, the piece people ask about most.
const SectionCard: FC<{ title: string; featured?: boolean; children: ReactNode }> = ({
    title,
    featured = false,
    children
}) => (
    <Paper
        elevation={featured ? 0 : 1}
        sx={{
            p: 3,
            ...(featured && {
                bgcolor: "var(--tint)",
                border: `1px solid ${GOLD}`
            })
        }}
    >
        <Typography fontWeight={600} sx={{ pb: 1.5 }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

// Numbered step: gold circle + title + body.
const Step: FC<{ n: number; title: string; children: ReactNode }> = ({ n, title, children }) => (
    <Box className="flex gap-3" sx={{ mb: 2.5 }}>
        <Box
            sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                bgcolor: GOLD,
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.25
            }}
        >
            {n}
        </Box>
        <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 15, pb: 0.5 }}>{title}</Typography>
            {children}
        </Box>
    </Box>
);

const tierMin = (tier: TierRow): number =>
    tier.min_projects ?? tier.min_distinct_projects ?? 0;

// The discount that applies to a quantity `n`: the tier with the largest
// breakpoint that is still <= n. Mirrors the backend's lookupTierDiscount so
// the live examples on this tab always agree with real quotes.
const discountFor = (tiers: TierRow[], n: number): number => {
    let pct = 0;
    let best = -1;
    tiers.forEach((t) => {
        const min = tierMin(t);
        if (min <= n && min > best) {
            best = min;
            pct = Number(t.discount_pct) || 0;
        }
    });
    return pct;
};

// Live tier ladder, e.g. "5+ products → 5% off", derived from the current
// config so the copy never drifts from what the other tabs actually say.
const TierLadder: FC<{ tiers: TierRow[]; noun: string }> = ({ tiers, noun }) => {
    const sorted = [...tiers].sort((a, b) => tierMin(a) - tierMin(b));
    if (sorted.length === 0) {
        return (
            <Typography color="var(--text-2)" sx={{ fontSize: 13, fontStyle: "italic", my: 1 }}>
                No tiers configured — this discount is currently always 0%.
            </Typography>
        );
    }
    return (
        <Box className="flex flex-col gap-1.5" sx={{ my: 1 }}>
            {sorted.map((t) => (
                <Box key={t.id} className="flex items-center gap-2">
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            bgcolor: GOLD,
                            flexShrink: 0
                        }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 150 }}>
                        {tierMin(t)}+ {noun}
                    </Typography>
                    <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
                        {Number(t.discount_pct)}% off
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

const LicenseList: FC<{ licenses: LicenseRow[] }> = ({ licenses }) => {
    if (licenses.length === 0) {
        return (
            <Typography color="var(--text-2)" sx={{ fontSize: 13, fontStyle: "italic", my: 1 }}>
                No licenses configured.
            </Typography>
        );
    }
    return (
        <Box className="flex flex-col gap-1.5" sx={{ my: 1 }}>
            {licenses.map((l) => (
                <Box key={l.id} className="flex items-center gap-2">
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            bgcolor: GOLD,
                            flexShrink: 0
                        }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 150 }}>
                        {l.display_name}
                    </Typography>
                    <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
                        {formatCurrency(l.price)} per video
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// One feature line on a package card.
const PackageFeature: FC<{ children: ReactNode }> = ({ children }) => (
    <Box className="flex items-start gap-1.5">
        <Typography sx={{ fontSize: 13, color: "var(--accent-text)", fontWeight: 700, lineHeight: 1.6 }}>
            ✓
        </Typography>
        <Typography sx={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6 }}>{children}</Typography>
    </Box>
);

// Live package catalog rendered pricing-table style — one card per active
// package, side by side, from the Packages tab config.
const PackageCards: FC<{ packages: PackageRow[] }> = ({ packages }) => {
    const visible = packages.filter((p) => p.active);
    if (visible.length === 0) {
        return (
            <Typography color="var(--text-2)" sx={{ fontSize: 13, fontStyle: "italic", my: 1 }}>
                No active packages configured — add them on the Packages tab.
            </Typography>
        );
    }
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                gap: 2,
                my: 1
            }}
        >
            {visible.map((p) => (
                <Paper
                    key={p.id}
                    elevation={0}
                    sx={{
                        p: 2.5,
                        border: "1px solid var(--border)",
                        borderTop: `3px solid ${GOLD}`,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }}>
                        {p.display_name}
                    </Typography>
                    <Box>
                        <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>
                            {formatCurrency(p.sell_price)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "var(--text-2)" }}>
                            flat, for the whole engagement
                        </Typography>
                    </Box>
                    <Box className="flex flex-col gap-0.5" sx={{ pt: 0.5 }}>
                        <PackageFeature>
                            Up to {p.max_skus} SKU{p.max_skus === 1 ? "" : "s"}
                        </PackageFeature>
                        <PackageFeature>
                            {p.term_months ? `${p.term_months}-month term` : "Single project"}
                        </PackageFeature>
                        {p.per_video_price !== null && (
                            <PackageFeature>
                                {formatCurrency(p.per_video_price)} per video
                            </PackageFeature>
                        )}
                        {p.payment_schedule && (
                            <PackageFeature>{p.payment_schedule}</PackageFeature>
                        )}
                        {p.priority_scheduling && (
                            <PackageFeature>Priority scheduling</PackageFeature>
                        )}
                        {p.rollover && <PackageFeature>Capacity rollover</PackageFeature>}
                    </Box>
                    {p.deliverables && (
                        <Typography
                            sx={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.55, mt: "auto", pt: 1 }}
                        >
                            {p.deliverables}
                        </Typography>
                    )}
                </Paper>
            ))}
        </Box>
    );
};

// One piece of the at-a-glance formula.
const FormulaChip: FC<{ children: ReactNode }> = ({ children }) => (
    <Box
        sx={{
            border: "1px solid var(--border)",
            borderRadius: 1.5,
            px: 1.25,
            py: 0.5,
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap"
        }}
    >
        {children}
    </Box>
);

const FormulaOp: FC<{ children: ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "var(--accent-text)" }}>{children}</Typography>
);

export const PricingExplanation: FC = () => {
    const { data, loading } = usePricing();
    const [selectedPriceId, setSelectedPriceId] = useState<number | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    if (loading || !data) return <DotLottleLoader />;

    const selectedPrice =
        data.content_type_prices.find((p) => p.id === selectedPriceId) ??
        data.content_type_prices[0] ??
        null;

    // Live percentages for the worked example (3 projects, 2 distinct sets).
    const examplePct = {
        projectCount: discountFor(data.project_count_tiers, 3),
        matching: discountFor(data.matching_product_tiers, 2)
    };

    return (
        <Box className="flex flex-col gap-4">
            {/* ── Packages — the flat-price selling layer ─────────────── */}
            <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 700, pb: 0.5 }}>Packages</Typography>
                <Para>
                    A package is attached to a <b>SOW</b> and its price is the billed amount for the
                    whole engagement — no matter how the individual project quotes add up. The
                    per-project math below keeps running as an internal &ldquo;value
                    delivered&rdquo; reference. Terms are snapshotted onto the SOW when attached, so
                    edits on the Packages tab only affect engagements sold afterwards.
                </Para>
                <PackageCards packages={data.packages} />
                <Para>
                    <b>SKUs are products</b>: each project consumes one slot per product (minimum
                    one per project), so &ldquo;15 SKUs&rdquo; is fifteen one-product projects.
                    Consumption is counted across every order under the SOW and shown wherever the
                    package appears — going over capacity or past the term shows a warning but{" "}
                    <b>never blocks</b> an order.
                </Para>
            </Box>

            {/* ── Ad-hoc — the à-la-carte engine ──────────────────────── */}
            <Box sx={{ pt: 1, borderTop: "1px solid var(--border)" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, pb: 0.5 }}>
                    Ad-hoc pricing
                </Typography>
                <Para>
                    Orders without a packaged SOW are priced à la carte, per project, by the engine
                    explained below. This is also the math behind the internal &ldquo;value
                    delivered&rdquo; reference on packaged engagements.
                </Para>
            </Box>

            <SectionCard title="How a quote is built">
                <Para>
                    Every project on an order is priced on its own, and the order total is simply the
                    sum of its projects. Each project&apos;s price is assembled from the tables on the
                    other tabs of this page — there are no hidden inputs. The recipe:
                </Para>
                <Box className="flex flex-wrap items-center gap-1.5" sx={{ my: 1.5 }}>
                    <FormulaChip>Content-type base</FormulaChip>
                    <FormulaOp>+</FormulaOp>
                    <FormulaChip>Products</FormulaChip>
                    <FormulaOp>+</FormulaOp>
                    <FormulaChip>Channels</FormulaChip>
                    <FormulaOp>+</FormulaOp>
                    <FormulaChip>Experts</FormulaChip>
                    <FormulaOp>+</FormulaOp>
                    <FormulaChip>License</FormulaChip>
                    <FormulaOp>−</FormulaOp>
                    <FormulaChip>one order-level discount</FormulaChip>
                    <FormulaOp>±</FormulaOp>
                    <FormulaChip>manual adjustment</FormulaChip>
                </Box>
                <Para>
                    The first five pieces add up to the project&apos;s <b>full price</b>. Exactly one
                    order-level discount is then applied (the distinct-vs-matching card below), and
                    any manual production adjustment is added last — after all discounts.
                </Para>
            </SectionCard>

            <SectionCard title="One order-level discount: distinct vs. matching projects" featured>
                <Para>
                    Projects on an order are grouped by their exact product set. The first project
                    with a given set is the <b>distinct</b> one; every additional project that reuses
                    the same set is a <b>matching</b> (duplicate) project. Each project then gets
                    exactly one of two discounts — they never stack:
                </Para>
                <Para>
                    <b>Distinct projects</b> get the project-count discount, based on the total number
                    of projects on the order:
                </Para>
                <TierLadder tiers={data.project_count_tiers} noun="projects on the order" />
                <Para>
                    <b>Matching projects</b> get the (usually much steeper) matching discount instead,
                    based on how many distinct product sets the order has:
                </Para>
                <TierLadder tiers={data.matching_product_tiers} noun="distinct product sets" />
                <Para>
                    The idea: a brand pays full freight once per unique product set, and re-runs of
                    the same set — a second cut, another channel push — are heavily discounted.
                </Para>
                <Box
                    sx={{
                        border: "1px solid var(--border)",
                        borderRadius: 1.5,
                        p: 2,
                        my: 1,
                        bgcolor: "var(--surface)"
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, pb: 0.5 }}>
                        Worked example (live percentages)
                    </Typography>
                    <Typography color="var(--text-2)" sx={{ fontSize: 13, lineHeight: 1.7 }}>
                        An order has three projects: A and B share the same product set, C uses a
                        different one. That makes 2 distinct sets across 3 projects. A and C are
                        distinct, so each gets the project-count discount for a 3-project order —
                        currently <b>{examplePct.projectCount}% off</b>. B duplicates A&apos;s set, so
                        it gets the matching discount for 2 distinct sets instead — currently{" "}
                        <b>{examplePct.matching}% off</b>.
                    </Typography>
                </Box>
                <Para>
                    An order&apos;s reported <b>savings</b> is the gap between the sum of full prices
                    and the sum of discounted prices. Manual adjustments are kept out of that number —
                    they are shown separately, not dressed up as savings.
                </Para>
            </SectionCard>

            <SectionCard title="Step by step: pricing one project">
                <Step n={1} title="Content-type base">
                    <Para>
                        A flat starting price determined by the project&apos;s content type — the
                        amounts on the <b>Content Types</b> tab. It is charged once per project
                        regardless of how many products or channels are attached.
                    </Para>
                </Step>
                <Step n={2} title="Products">
                    <Para>
                        The content type&apos;s <b>per-product base price</b> multiplied by the number
                        of products on the project — charged at face value, with no per-project volume
                        discount.
                    </Para>
                </Step>
                <Step n={3} title="Channels">
                    <Para>
                        Each selected channel adds its price for the project&apos;s content type, from
                        the <b>Channels</b> tab. If the project has option groups (say, 3 colors
                        and 2 sizes), the channel money is multiplied by the number of variations —
                        3 × 2 = 6 in that example — because each variation is another deliverable per
                        channel. Like products, channel money carries no per-project volume discount.
                    </Para>
                </Step>
                <Step n={4} title="Experts">
                    <Para>
                        Once experts are confirmed on the project, their actual rates are summed. If
                        the project plans to use experts but none are confirmed yet, the quote carries
                        a standard placeholder (currently $2,000 for one expert) that is replaced by
                        real rates as confirmations come in. Projects that opt out of experts add
                        nothing here.
                    </Para>
                </Step>
                <Step n={5} title="License">
                    <Para>
                        The license selected on the project, from the <b>Licenses</b> tab. Licenses
                        are per video: the price is charged once per project, regardless of how many
                        products it covers:
                    </Para>
                    <LicenseList licenses={data.licenses} />
                </Step>
            </SectionCard>

            <SectionCard title="What the content type base covers">
                <Para>
                    Every content type&apos;s base price carries its own <b>cost sheet</b> — the
                    role-hours and flat items the price is built to cover. The sheet is shown to
                    clients as the breakdown behind the base price, and is edited per content type
                    on the <b>Content Types</b> tab (expand a row). Pick a content type and
                    expand to see its current sheet:
                </Para>
                <Box className="flex items-center gap-2" sx={{ mb: 1 }}>
                    <Select
                        size="small"
                        value={selectedPrice?.id ?? ""}
                        onChange={(e) => setSelectedPriceId(Number(e.target.value))}
                        sx={{ minWidth: 260 }}
                    >
                        {data.content_type_prices.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                                {titleizeName(p.content_type?.name ?? `#${p.content_type_id}`)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        aria-expanded={sheetOpen}
                        onClick={() => setSheetOpen((v) => !v)}
                        sx={{ fontSize: 13, color: "var(--accent-text)" }}
                    >
                        {sheetOpen ? "hide cost sheet ▴" : "show cost sheet ▾"}
                    </Link>
                </Box>
                <Collapse in={sheetOpen}>
                    <BaseRecipeTable items={selectedPrice?.line_items ?? null} />
                </Collapse>
                <Para>
                    Editing is typically the largest share of labor hours and is where scope creep
                    hits hardest — extra cutdowns, format changes and revision rounds all land in
                    edit time. That is the argument for holding the base price and pricing scope
                    changes instead of absorbing them. Rates run well under agency benchmarks,
                    which is margin room — not a reason to discount.
                </Para>
            </SectionCard>

            <SectionCard title="When prices change — and when they don't">
                <Para>
                    Prices are <b>snapshotted</b>: a project&apos;s quote is computed and stored when
                    the project is saved. Editing the tables on this page changes future quotes only —
                    existing projects keep their stored price until they are edited and re-saved.
                    That is what the yellow banner on the other tabs is about.
                </Para>
                <Para>
                    Adding or removing a project on an order re-prices <b>all</b> of that order&apos;s
                    projects, because the project count — and therefore the order-level discount tier —
                    may have shifted for every one of them.
                </Para>
                <Para>
                    When a project is scheduled (a film date is set), its price <b>locks</b>: one final
                    snapshot is taken and the price stops moving from that point on, turning the
                    estimate into the actual. Manual production adjustments remain internal-only and
                    are always applied after every discount.
                </Para>
            </SectionCard>
        </Box>
    );
};

export default PricingExplanation;
